import _ from 'lodash'

/**
 * 이미지/비디오 주소를 cdn 주소로 변환한다.
 * 기존 URL에 CDN 주소가 포함되어있을 경우 추가하지 않도록 예외처리 하였음.
 * @constructor
 * @function
 * @param {string} mediaSrc - cdn 주소가 포함되지 않은 이미지/비디오 주소
 * @return {String} CDN 주소로 변환된 이미지/비디오 주소
 */
const getCdnUrl = (mediaSrc) => {
  let mediaUrl = process.env.WEB_BASE_URL + '/images/tempLy/noimage.png'
  if (mediaSrc) {
    if (mediaSrc.includes(process.env.CDN_BASE_URL)) {
      mediaUrl = mediaSrc
    } else {
      let url = process.env.CDN_BASE_URL
      url = (mediaSrc.charAt(0) !== '/' ? url + '/' : url) + mediaSrc
      mediaUrl = _.unescape(url)
    }
  }
  return isEncoded(mediaUrl) ? mediaUrl : encodeURI(mediaUrl)
}

/**
 * 이미지 주소에 format, quality, resizing parameter를 적용한다.
 * @constructor
 * @function
 * @param {String} imageSrc - image URL
 * @param {String} imageResize - resize
 * @param {number} imageQuality -  image quality
 * @param {String} imageFormat - image format
 * @param {Boolean} isAutorotate - 자동회전 여부
 * @param {String} cropInfo - 좌상단을 기준으로 원하는 영역만큼 이미지를 잘라낸다. 영역은 width x height{+-}x{+-}y{%} 로 표현한다. 이미지 중앙을 기준으로 하고 싶은 경우 cropcenter명령어를 사용한다.
 * @return {String} 변환된 이미지 주소
 */
const getResizedImgUrl = (imageSrc, imageResize, imageQuality, imageFormat, isAutorotate, cropInfo) => {
  if (imageSrc) {
    let resultSrc = `${isEncoded(imageSrc) ? imageSrc : encodeURI(imageSrc)}${
      isAutorotate ? '/dims/autorate/on' : ''
    }${
      imageResize ? `/dims/resize/${imageResize}` : ''
    }${
      imageQuality ? `/dims/quality/${imageQuality}` : ''
    }${
      imageFormat ? (imageFormat === 'avifWithWebp' ? '/dims/format/avif;fallback=webp' : `/dims/format/${imageFormat}`) : ''
    }${cropInfo ?? ''}`

    // 'optimize' 추가 시 undefined 방지
    if (!resultSrc.includes('optimize')) {
      resultSrc += '/dims/optimize'
    }

    // undefined 방지를 위해 조건 추가
    const optimizedResultSrc = getUniqueUrlWithWord(resultSrc, '/optimize') ?? resultSrc
    return getUniqueUrlWithWord(optimizedResultSrc, '/dims') ?? optimizedResultSrc
  } else {
    return null
  }
}

/**
 * 비디오 주소에 format, resizing parameter를 적용한다.
 * @constructor
 * @function
 * @param {String} videoSrc - video URL
 * @param {String} videoResize - resize
 * @Param {Boolean} fastStart - fastStart 추가여부
 * @param {String} videoFormat - video format
 * @param {String} trimRange - trim Range( ex)1-5 )
 * @return {String} 변환된 비디오 주소
 */
const getResizedVideoUrl = (videoSrc, videoResize, fastStart, videoFormat, trimRange) => {
  if (videoSrc) {
    return videoSrc +
    (videoResize
      ? '/xcdr/preset/_' + videoResize + 'p'
      : '') +
    (trimRange
      ? '/trim/' + trimRange
      : '') +
    (videoFormat === 'webm'
      ? '/xcdr/preset/_mp42webm/options/video.codec=vp9/.webm'
      : '') +
    (fastStart
      ? '/faststart/true'
      : '')
  } else {
    return null
  }
}

/**
 * 비디오에서 캡쳐된 이미지를 Lambda 방식으로 추출한다.
 * @function
 * @param {String} videoSrc - Video URL (예: /file/DS/20260325/video.mp4)
 * @param {Number|String} imageResize - 캡쳐될 이미지의 resize info (예: 300, 850)
 * @return {String} 캡쳐될 이미지의 주소 (예: /file/resize/DS/20260325/capture/300/video.mp4)
 */
const getCapturedImageUrl = (videoSrc, imageResize) => {
  if (!videoSrc) {
    return null
  }

  let cleanSrc = videoSrc

  // 이미 CDN URL인 경우 경로만 추출
  if (cleanSrc.startsWith('http://') || cleanSrc.startsWith('https://')) {
    try {
      const url = new URL(cleanSrc)
      cleanSrc = url.pathname
    } catch (e) {
      console.warn('[getCapturedImageUrl] Invalid URL:', cleanSrc)
      return null
    }
  }

  // xcdr 레거시 경로 제거
  cleanSrc = cleanSrc.replace(/\/xcdr\/.*$/, '')

  // dims 경로 제거
  cleanSrc = cleanSrc.replace(/\/dims\/.*$/, '')

  // /file/로 시작하지 않으면 처리 불가
  if (!cleanSrc.startsWith('/file/')) {
    console.warn('[getCapturedImageUrl] Invalid path structure:', cleanSrc)
    return cleanSrc
  }

  const pathParts = cleanSrc.replace(/^\/file\//, '').split('/')

  if (pathParts.length < 3) {
    console.warn('[getCapturedImageUrl] Invalid path structure:', cleanSrc)
    return cleanSrc
  }

  const [folder, date, ...filenameParts] = pathParts
  const filename = filenameParts.join('/')

  return `/file/resize/${folder}/${date}/capture/${imageResize}/${filename}`
}

/**
 * 특정 키워드를 유일하게 만든다. (/dims, /optimize 등)
 * @constructor
 * @function
 * @param {String} src - Image/Video URL
 */
const getUniqueUrlWithWord = (url, word) => {
  const splitedUrl = url.split(word)
  let mediaUrl = ''
  for (let i = 0; i < splitedUrl.length; i++) {
    switch (i) {
      case 0:
        mediaUrl = splitedUrl[i]
        break
      case 1:
        mediaUrl += word + splitedUrl[i]
        break
      default:
        mediaUrl += splitedUrl[i]
        break
    }
  }
  return mediaUrl
}

/**
 * 인코딩 유무를 체크한다.
 * @function
 * @param {String} url - Image/Video URL
 */
const isEncoded = (url) => {
  try {
    return url !== decodeURIComponent(url)
  } catch (e) {
    return false
  }
}

/**
 * 이미지 경로를 Lambda resize 경로로 변환한다.
 * @function
 * @param {String} src - 이미지 경로 (예: /file/DS/20260203/filename.jpg 또는 full CDN URL)
 * @param {String} detail - 이미지 용도 (예: 'banner', 'thumb', 'thumbnail')
 * @param {String|Number} size - 리사이즈 크기 (예: '850', '300')
 * @return {String} Lambda resize 경로 (예: /file/resize/DS/20260203/banner/850/filename.jpg)
 */
const getLambdaResizedImgUrl = (src, detail, size) => {
  if (!src || typeof src !== 'string') {
    return ''
  }

  let cleanSrc = src

  if (cleanSrc.startsWith('http://') || cleanSrc.startsWith('https://')) {
    try {
      const url = new URL(cleanSrc)
      cleanSrc = url.pathname
    } catch (e) {
      console.warn('[getLambdaResizedImgUrl] Invalid URL:', cleanSrc)
      return ''
    }
  }

  cleanSrc = cleanSrc.replace(/\/(dims|xcdr)\/.*$/, '')

  if (!cleanSrc.startsWith('/file/')) {
    return cleanSrc
  }

  const pathParts = cleanSrc.replace(/^\/file\//, '').split('/')

  if (pathParts.length < 3) {
    console.warn('[getLambdaResizedImgUrl] Invalid path structure:', cleanSrc)
    return cleanSrc
  }

  const [folder, date, ...filenameParts] = pathParts
  const filename = filenameParts.join('/')

  return `/file/resize/${folder}/${date}/${detail}/${size}/${filename}`
}

export {
  getCdnUrl,
  getResizedImgUrl,
  getResizedVideoUrl,
  getCapturedImageUrl,
  getLambdaResizedImgUrl
}
